import React, { useState, useEffect, useMemo } from "react";
import { Plus, Car, Trash2, Search, Pencil } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { vehicles as api, residents as residentsApi, parkingSlots as parkingSlotsApi, type Vehicle, type Resident, type ParkingSlot, type VehicleLookupResult, type ApiError, type CsvImportResult } from "../../services/api";
import DataGrid from "../../components/DataGrid";
import SearchSelect from "../../components/SearchSelect";
import BulkImportExport from "../../components/BulkImportExport";
import { downloadBlob } from "../../utils/downloadBlob";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const INIT = { vehicleType: "CAR", registrationNumber: "", makeModel: "", color: "", residentId: "" };

const canLookup = (role: string | undefined) =>
  role === "SUPER_ADMIN" || role === "SOCIETY_ADMIN" || role === "STAFF" || role === "GUARD";

const picksResident = (role: string | undefined) =>
  role === "SUPER_ADMIN" || role === "SOCIETY_ADMIN" || role === "STAFF";

const VehiclesPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmVehicle, setConfirmVehicle] = useState<Vehicle | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [residentList, setResidentList] = useState<Resident[]>([]);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const [assignedSlots, setAssignedSlots] = useState<ParkingSlot[]>([]);

  const [lookupPlate, setLookupPlate] = useState("");
  const [lookupResult, setLookupResult] = useState<VehicleLookupResult | null | undefined>(undefined);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(null);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<"asc" | "desc" | undefined>(undefined);
  const handleSortChange = (field: string) => {
    if (sortField !== field) { setSortField(field); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortField(undefined); setSortDir(undefined); }
  };

  const handleImport = async (file: File) => {
    if (!societyId) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await api.importFile(societyId, file);
      setImportResult(result);
      api.list(societyId).then(setItems).catch(() => {});
    } catch (err) {
      setImportResult({
        created: 0,
        failed: 1,
        errors: [(err as ApiError).message || "Import failed"],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    if (!societyId) return;
    setExporting(true);
    try {
      const blob = await api.exportCsv(societyId);
      downloadBlob(blob, "vehicles-export.csv");
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !lookupPlate.trim()) return;
    setLookupLoading(true); setLookupError(""); setLookupResult(undefined);
    try {
      const result = await api.lookup(societyId, lookupPlate.trim());
      setLookupResult(result);
    } catch (err) {
      setLookupError((err as ApiError).message || "Lookup failed.");
    } finally { setLookupLoading(false); }
  };

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  useEffect(() => {
    if (!societyId || residentList.length > 0) return;
    setResidentsLoading(true);
    residentsApi.list(societyId, { size: 500 })
      .then(page => setResidentList(page.content))
      .catch(() => {})
      .finally(() => setResidentsLoading(false));
  }, [societyId, residentList.length]);

  useEffect(() => {
    if (!societyId) return;
    parkingSlotsApi.list(societyId, { status: "ASSIGNED", size: 500 })
      .then(page => setAssignedSlots(page.content))
      .catch(() => {});
  }, [societyId]);

  const ownerFor = (v: Vehicle) => residentList.find(r => r.id === v.residentId);

  // Plates get typed inconsistently ("KA 01 AB 1234" vs "KA-01-AB-1234"), so compare
  // on letters/digits only rather than requiring an exact string match.
  const normalizePlate = (value: string | undefined) => (value ?? "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  const slotFor = (v: Vehicle) => assignedSlots.find(s => normalizePlate(s.vehicleNumber) === normalizePlate(v.registrationNumber));

  const sortValue = (v: Vehicle, field: string): string => {
    switch (field) {
      case "registrationNumber": return v.registrationNumber ?? "";
      case "vehicleType": return v.vehicleType ?? "";
      case "makeModel": return v.makeModel ?? "";
      case "color": return v.color ?? "";
      case "owner": return ownerFor(v)?.fullName ?? "";
      case "parkingSlot": return slotFor(v)?.slotNumber ?? "";
      default: return "";
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortField) return items;
    const dir = sortDir === "desc" ? -1 : 1;
    return [...items].sort((a, b) =>
      sortValue(a, sortField).localeCompare(sortValue(b, sortField), undefined, { numeric: true, sensitivity: "base" }) * dir,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortField, sortDir, residentList, assignedSlots]);

  const openAdd = () => { setFormError(""); setForm(INIT); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(INIT); setFormError(""); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError("");
    if (picksResident(user?.role) && !form.residentId) {
      setSaving(false);
      setFormError("Please select a resident.");
      return;
    }
    try {
      const created = await api.create(societyId, {
        vehicleType: form.vehicleType as Vehicle["vehicleType"],
        registrationNumber: form.registrationNumber,
        makeModel: form.makeModel || undefined,
        color: form.color || undefined,
        residentId: picksResident(user?.role) ? form.residentId : undefined,
      });
      setItems(p => [created, ...p]);
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to register vehicle.");
    } finally { setSaving(false); }
  };

  const openEdit = (v: Vehicle) => {
    setEditError("");
    setEditForm({
      vehicleType: v.vehicleType,
      registrationNumber: v.registrationNumber,
      makeModel: v.makeModel || "",
      color: v.color || "",
      residentId: v.residentId,
    });
    setEditId(v.id);
  };
  const closeEdit = () => { setEditId(null); setEditForm(INIT); setEditError(""); };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editId) return;
    setEditSaving(true); setEditError("");
    if (picksResident(user?.role) && !editForm.residentId) {
      setEditSaving(false);
      setEditError("Please select a resident.");
      return;
    }
    try {
      const updated = await api.update(societyId, editId, {
        vehicleType: editForm.vehicleType as Vehicle["vehicleType"],
        registrationNumber: editForm.registrationNumber,
        makeModel: editForm.makeModel || undefined,
        color: editForm.color || undefined,
        residentId: picksResident(user?.role) ? editForm.residentId : undefined,
      });
      setItems(p => p.map(v => (v.id === updated.id ? updated : v)));
      closeEdit();
    } catch (err) {
      setEditError((err as ApiError).message || "Failed to update vehicle.");
    } finally { setEditSaving(false); }
  };

  const confirmDelete = async () => {
    if (!societyId || !confirmVehicle) return;
    setDeletingId(confirmVehicle.id);
    try {
      await api.delete(societyId, confirmVehicle.id);
      setItems(p => p.filter(v => v.id !== confirmVehicle.id));
      setConfirmVehicle(null);
    } catch { /* ignore */ }
    finally { setDeletingId(null); }
  };

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Vehicles
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} registered vehicles
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {picksResident(user?.role) && (
            <BulkImportExport
              onImport={handleImport}
              onExport={handleExport}
              importing={importing}
              exporting={exporting}
              acceptExcel
            />
          )}
          <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Register Vehicle
          </button>
        </div>
      </div>

      {importResult && (
        <div
          style={{
            padding: "10px 16px",
            background: "rgba(34,197,94,0.08)",
            border: "1px solid rgba(34,197,94,0.25)",
            borderRadius: 10,
            fontSize: 13,
            color: "#16A34A",
            marginBottom: 20,
          }}
        >
          Imported {importResult.created} vehicles
          {importResult.failed > 0
            ? `, ${importResult.failed} failed: ${importResult.errors.join("; ")}`
            : "."}
        </div>
      )}

      {canLookup(user?.role) && (
        <div className="nx-card" style={{ padding: 18, marginBottom: 20 }}>
          <h3 style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 700, color: "var(--nx-text-primary)", marginBottom: 12 }}>
            Lookup by Plate Number
          </h3>
          <form onSubmit={handleLookup} style={{ display: "flex", gap: 8 }}>
            <input
              value={lookupPlate}
              onChange={e => setLookupPlate(e.target.value)}
              placeholder="KA01AB1234"
              style={{ ...inp, flex: 1 }}
            />
            <button type="submit" className="nx-btn nx-btn-primary nx-btn-sm" disabled={lookupLoading} style={{ gap: 6 }}>
              <Search size={14} /> {lookupLoading ? "Searching…" : "Search"}
            </button>
          </form>
          {lookupError && <p style={{ fontSize: 12, color: "#EF4444", marginTop: 10 }}>{lookupError}</p>}
          {lookupResult === null && !lookupError && (
            <p style={{ fontSize: 12, color: "var(--nx-text-muted)", marginTop: 10 }}>No vehicle found with that registration number.</p>
          )}
          {lookupResult && (
            <div style={{ marginTop: 12, padding: "10px 12px", background: "var(--nx-surface-3)", borderRadius: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: "var(--nx-text-primary)", marginBottom: 4 }}>
                {lookupResult.registrationNumber} — {lookupResult.makeModel || lookupResult.vehicleType}
                {lookupResult.color ? ` (${lookupResult.color})` : ""}
              </div>
              <div style={{ color: "var(--nx-text-secondary)" }}>
                Owner: {lookupResult.residentName || "—"}
                {lookupResult.residentPhone ? ` · ${lookupResult.residentPhone}` : ""}
              </div>
              <div style={{ color: "var(--nx-text-muted)" }}>
                Unit: {lookupResult.blockName ? `${lookupResult.blockName} - ` : ""}{lookupResult.unitNumber || "—"}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            { key: "registrationNumber", header: "Registration", cellStyle: { fontWeight: 600 }, sortable: true, render: (v: Vehicle) => v.registrationNumber },
            { key: "vehicleType", header: "Type", sortable: true, render: (v: Vehicle) => v.vehicleType },
            { key: "makeModel", header: "Make/Model", cellStyle: { color: "var(--nx-text-secondary)" }, sortable: true, render: (v: Vehicle) => v.makeModel || "—" },
            { key: "color", header: "Color", cellStyle: { color: "var(--nx-text-muted)" }, sortable: true, render: (v: Vehicle) => v.color || "—" },
            { key: "owner", header: "Owner", cellStyle: { color: "var(--nx-text-secondary)" }, sortable: true, render: (v: Vehicle) => ownerFor(v)?.fullName || "—" },
            {
              key: "unit", header: "Unit",
              cellStyle: { color: "var(--nx-text-muted)" },
              render: (v: Vehicle) => {
                const r = ownerFor(v);
                if (!r) return "—";
                return r.blockName ? `${r.blockName} — ${r.unitNumber ?? ""}` : (r.unitNumber || "—");
              },
            },
            {
              key: "parkingSlot", header: "Parking Slot",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              render: (v: Vehicle) => slotFor(v)?.slotNumber || "—",
            },
            {
              key: "actions", header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (v: Vehicle) => (
                <>
                  <button
                    onClick={() => openEdit(v)}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 6, color: "var(--nx-text-muted)" }}
                    title="Edit vehicle"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => setConfirmVehicle(v)}
                    disabled={deletingId === v.id}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 6, color: "#EF4444" }}
                    title="Remove vehicle"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={sortedItems}
          rowKey={v => v.id}
          loading={loading}
          emptyIcon={<Car size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText="No vehicles registered."
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={handleSortChange}
        />
      </div>

      <ConfirmDialog
        open={confirmVehicle !== null}
        variant="danger"
        title={`Remove "${confirmVehicle?.registrationNumber}"?`}
        message="This vehicle will no longer be recognized at the gate."
        confirmLabel="Remove"
        isLoading={deletingId !== null}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmVehicle(null)}
      />

      {showForm && (
        <Modal
          title="Register a Vehicle"
          submitLabel="Register"
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          {picksResident(user?.role) && (
            <div>
              <label style={lbl}>Resident *</label>
              {residentsLoading ? (
                <div style={{ ...inp, color: "var(--nx-text-muted)" }}>Loading residents…</div>
              ) : (
                <SearchSelect
                  value={form.residentId}
                  onChange={v => setForm(p => ({ ...p, residentId: v }))}
                  options={residentList.map(r => ({
                    value: r.id,
                    label: r.fullName + (r.unitNumber ? ` (${r.unitNumber})` : ""),
                  }))}
                  placeholder={residentList.length === 0 ? "No residents available" : "Search residents…"}
                  emptyText="No matching residents"
                  required
                  disabled={residentList.length === 0}
                />
              )}
            </div>
          )}
          <div>
            <label style={lbl}>Type</label>
            <select value={form.vehicleType} onChange={e => setForm(p => ({ ...p, vehicleType: e.target.value }))} style={inp}>
              <option value="CAR">Car</option>
              <option value="BIKE">Bike</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Registration Number *</label>
            <input value={form.registrationNumber} onChange={e => setForm(p => ({ ...p, registrationNumber: e.target.value }))} required placeholder="KA-01-AB-1234" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Make/Model</label>
            <input value={form.makeModel} onChange={e => setForm(p => ({ ...p, makeModel: e.target.value }))} placeholder="Honda City" style={inp} />
          </div>
          <div>
            <label style={lbl}>Color</label>
            <input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} placeholder="White" style={inp} />
          </div>
        </Modal>
      )}

      {editId && (
        <Modal
          title="Edit Vehicle"
          submitLabel="Save Changes"
          error={editError}
          isLoading={editSaving}
          onClose={closeEdit}
          onSubmit={handleEditSubmit}
        >
          {picksResident(user?.role) && (
            <div>
              <label style={lbl}>Resident *</label>
              {residentsLoading ? (
                <div style={{ ...inp, color: "var(--nx-text-muted)" }}>Loading residents…</div>
              ) : (
                <SearchSelect
                  value={editForm.residentId}
                  onChange={v => setEditForm(p => ({ ...p, residentId: v }))}
                  options={residentList.map(r => ({
                    value: r.id,
                    label: r.fullName + (r.unitNumber ? ` (${r.unitNumber})` : ""),
                  }))}
                  placeholder={residentList.length === 0 ? "No residents available" : "Search residents…"}
                  emptyText="No matching residents"
                  required
                  disabled={residentList.length === 0}
                />
              )}
            </div>
          )}
          <div>
            <label style={lbl}>Type</label>
            <select value={editForm.vehicleType} onChange={e => setEditForm(p => ({ ...p, vehicleType: e.target.value }))} style={inp}>
              <option value="CAR">Car</option>
              <option value="BIKE">Bike</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Registration Number *</label>
            <input value={editForm.registrationNumber} onChange={e => setEditForm(p => ({ ...p, registrationNumber: e.target.value }))} required placeholder="KA-01-AB-1234" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Make/Model</label>
            <input value={editForm.makeModel} onChange={e => setEditForm(p => ({ ...p, makeModel: e.target.value }))} placeholder="Honda City" style={inp} />
          </div>
          <div>
            <label style={lbl}>Color</label>
            <input value={editForm.color} onChange={e => setEditForm(p => ({ ...p, color: e.target.value }))} placeholder="White" style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default VehiclesPage;
